import { useTranslation } from 'react-i18next';
import { Alert, Drawer, Group, Loader, Stack, Table, Text } from '@mantine/core';
import { useMovements } from './inventoryApi';
import { STOCK_MOVEMENT_TYPE, type ItemStockDto, type StockMovementType } from './types';

interface Props {
  stock: ItemStockDto | null;
  opened: boolean;
  onClose: () => void;
}

const TYPE_LABELS: Record<StockMovementType, string> = {
  [STOCK_MOVEMENT_TYPE.OPENING_COUNT]: 'inventory.types.openingCount',
  [STOCK_MOVEMENT_TYPE.RECOUNT]:       'inventory.types.recount',
  [STOCK_MOVEMENT_TYPE.WASTAGE]:       'inventory.types.wastage',
  [STOCK_MOVEMENT_TYPE.SALE]:          'inventory.types.sale',
  [STOCK_MOVEMENT_TYPE.SALE_VOID]:     'inventory.types.saleVoid',
  [STOCK_MOVEMENT_TYPE.GRN]:           'inventory.types.grn',
  [STOCK_MOVEMENT_TYPE.TRANSFER]:      'inventory.types.transfer',
  [STOCK_MOVEMENT_TYPE.MANUAL_ADJUST]: 'inventory.types.manualAdjust',
};

export function MovementHistoryDrawer({ stock, opened, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useMovements(
    stock?.branchId ?? null,
    stock?.itemId ?? null,
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Text fw={600}>
          {t('inventory.history.title')} — {stock?.itemNameAr ?? ''}
        </Text>
      }
    >
      {isLoading && (
        <Group justify="center" py="xl"><Loader size="sm" /></Group>
      )}
      {error && (
        <Alert color="red">
          {error instanceof Error ? error.message : t('inventory.history.failed')}
        </Alert>
      )}
      {data && data.length === 0 && (
        <Alert color="gray">{t('inventory.history.empty')}</Alert>
      )}
      {data && data.length > 0 && (
        <Stack gap="xs">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('inventory.history.time')}</Table.Th>
                <Table.Th>{t('inventory.history.type')}</Table.Th>
                <Table.Th className="text-end">{t('inventory.history.delta')}</Table.Th>
                <Table.Th className="text-end">{t('inventory.history.after')}</Table.Th>
                <Table.Th>{t('inventory.history.note')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((m) => (
                <Table.Tr key={m.stockMovementId}>
                  <Table.Td className="font-numeric whitespace-nowrap">
                    {formatTime(m.recordedAt, i18n.language)}
                  </Table.Td>
                  <Table.Td>{t(TYPE_LABELS[m.type])}</Table.Td>
                  <Table.Td className={`font-numeric text-end ${m.quantityDelta < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {m.quantityDelta > 0 ? '+' : ''}{m.quantityDelta}
                  </Table.Td>
                  <Table.Td className="font-numeric text-end">{m.onHandAfter}</Table.Td>
                  <Table.Td>
                    {m.note ?? (m.relatedOrderId
                      ? t('inventory.history.fromOrder')
                      : '—')}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </Drawer>
  );
}

function formatTime(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-PS' : 'en-GB', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    numberingSystem: 'latn',
  }).format(new Date(iso));
}
